// Which transcription engine is active. Resolution order:
//   1. explicit ?engine=deepgram|google|webspeech  → always wins (A/B + forcing)
//   2. REACT_APP_STT_ENGINE (primary) → forces that engine for EVERY browser (this is how
//      "Deepgram for everyone" is switched on — Chrome included, so the browser gate never
//      shows). ?engine=webspeech still overrides it for your own testing.
//   3. otherwise auto-route: Chrome → free 'webspeech'; any other browser → the
//      REACT_APP_STT_FALLBACK engine if set.
// Shared so the game wiring and the debug indicator agree on one source of truth.

import { isSupportedBrowser } from '../lib/env'

export type SttEngine = 'webspeech' | 'deepgram' | 'google'

// NB: use `process.env.REACT_APP_*` directly — CRA inlines it to a string literal at
// build time. Do NOT guard with `typeof process !== 'undefined'`: webpack doesn't define
// a bare global `process`, so that guard is false at runtime and nukes the inlined value.
const PRIMARY = process.env.REACT_APP_STT_ENGINE || '' // if set, forces this engine for ALL browsers
const FALLBACK = process.env.REACT_APP_STT_FALLBACK || '' // non-Chrome fallback (used when no PRIMARY)

export const getSttEngine = (): SttEngine => {
  const e = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('engine') : null
  if (e === 'deepgram' || e === 'google') return e
  if (e === 'webspeech') return 'webspeech'
  // Primary engine forced for EVERYONE (Deepgram-for-all — single clean engine, no confound).
  if (PRIMARY === 'deepgram' || PRIMARY === 'google') return PRIMARY
  // Otherwise: non-Chrome routes to the configured fallback; Chrome keeps free Web Speech.
  if ((FALLBACK === 'deepgram' || FALLBACK === 'google') && !isSupportedBrowser()) return FALLBACK
  return 'webspeech'
}

export const sttEngineLabel = (e: SttEngine): string => {
  switch (e) {
    case 'deepgram':
      return 'Deepgram (relay)'
    case 'google':
      return 'Google STT (relay)'
    default:
      return 'Chrome Web Speech (native)'
  }
}
